/* ═════════════════════════════════════════════════════════════════
   Partner · File a Correction (dynamic form)
   ═════════════════════════════════════════════════════════════════
   Route: /corrections/submit/:slug
   Renders the schema for one of the 5 correction forms, collects the
   15+ fields, the three declarations, and the three mandatory
   uploads, and POSTs to /api/bureaucratic/corrections.
   ═════════════════════════════════════════════════════════════════ */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, ShieldCheck, Loader2, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CorrectionSubmit() {
  const { slug, token } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [share, setShare] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [values, setValues] = useState({});
  const [declarations, setDeclarations] = useState([]);
  const [uploads, setUploads] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (token) {
          const { data } = await axios.get(`${API}/bureaucratic/shares/${token}`, { withCredentials: true });
          if (!alive) return;
          setShare(data.share); setForm(data.form);
          setDeclarations(new Array(data.form.declarations.length).fill(false));
          if (["completed", "verified", "rejected"].includes(data.share.status)) {
            setSubmitted({ id: data.share.submission_id, reference: (data.share.submission_id || "").slice(-6).toUpperCase(), srn: data.share.srn, already: true });
          }
          return;
        }
        const { data } = await axios.get(`${API}/bureaucratic/forms`);
        const f = (data.forms || []).find((x) => x.slug === slug);
        if (!f) { toast.error("Form not found"); nav("/admin/bureaucratic"); return; }
        if (alive) { setForm(f); setDeclarations(new Array(f.declarations.length).fill(false)); }
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (token) { setGateError(d?.message || d || "This mandate link could not be opened"); return; }
        toast.error("Unable to load the form schema");
      }
    })();
    return () => { alive = false; };
  }, [slug, token, nav]);

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const setUpload = (k, file) => setUploads((prev) => ({ ...prev, [k]: file }));
  const toggleDec = (i) => setDeclarations((d) => d.map((v, j) => j === i ? !v : v));

  const submit = async () => {
    if (!form) return;
    const missing = form.fields.filter(
      (f) => f.required && !(values[f.key] || "").toString().trim());
    if (missing.length) {
      toast.error(`Missing: ${missing.map((f) => f.label).slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`);
      return;
    }
    if (!declarations.every(Boolean)) {
      toast.error("Please tick every Declaration of Truth"); return;
    }
    for (const u of form.uploads) {
      if (!uploads[u.key]) { toast.error(`Please upload: ${u.label}`); return; }
    }
    const fd = new FormData();
    fd.append("form_slug", form.slug);
    fd.append("values_json", JSON.stringify(values));
    if (token) fd.append("share_token", token);
    fd.append("id_upload", uploads["id_upload"]);
    fd.append("proof_of_mistake", uploads["proof_of_mistake"]);
    fd.append("covering_letter", uploads["covering_letter"]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bureaucratic/corrections`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(data);
      toast.success(`Filed · Ref ${data.reference}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Filing failed");
    } finally { setBusy(false); }
  };

  if (gateError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F0EA] px-6">
        <div className="max-w-lg w-full text-center border bg-white/70 p-8" style={{ borderColor: "#B23A48" }}
             data-testid="fill-gate-error">
          <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#B23A48]">Mandate Link · Access Denied</div>
          <h1 className="font-serif text-2xl text-[#161A1E] mt-2 mb-3">Bound to another signatory</h1>
          <p className="text-[13px] text-[#4A5058] font-body">{String(gateError)}</p>
          <Link to="/account" className="inline-block mt-5 font-mono text-[9px] uppercase tracking-[0.28em] text-[#6B6F76] hover:text-black">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F0EA] text-[#6B6F76] font-mono text-sm">
        Loading schema…
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F0EA] px-6">
        <div className="max-w-lg w-full text-center border bg-white/70 p-8"
             style={{ borderColor: "#4A5058" }}>
          <ShieldCheck size={40} className="mx-auto text-[#2FB999] mb-3" />
          <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#7A8590]">
            Vinci Compliance Capsule
          </div>
          <h1 className="font-serif text-2xl text-[#161A1E] mt-2 mb-3">
            {submitted.already ? "Instrument Already Filed" : "Instrument Filed"}
          </h1>
          {submitted.srn && (
            <div className="font-mono text-[13px] tracking-[0.14em] text-[#8A6D1F] font-bold mb-2" data-testid="submit-srn">
              {submitted.srn}
            </div>
          )}
          <p className="text-[13px] text-[#4A5058] font-body">
            Reference <span className="font-mono text-[#161A1E]">{submitted.reference}</span>.
            Executive Command will scrutinise your submission and respond in due course.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <a href={`${API}/bureaucratic/corrections/${submitted.id}/snapshot`}
               target="_blank" rel="noreferrer"
               data-testid="submit-view-snapshot"
               className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.28em] border border-[#4A5058] text-[#2C3138] px-4 py-2 rounded-sm hover:bg-[#E4E6E8]">
              <ExternalLink size={12} /> View Snapshot PDF
            </a>
            <Link to="/account" className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#6B6F76] hover:text-black">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F2F0EA" }}>
      <div className="border-b" style={{ background: "#1F2226", borderColor: "#4A5058" }}>
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to={share ? "/account" : "/admin/bureaucratic"}
                className="flex items-center gap-1.5 text-[10px] font-grotesk uppercase tracking-[0.28em] text-[#C4C8CC] hover:text-white">
            <ArrowLeft size={12} /> {share ? "Dashboard" : "Library"}
          </Link>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#7A8590] font-mono">
              {form.tone}
            </div>
            <h1 className="font-serif text-xl text-[#E4E6E8] tracking-wide mt-1">
              {form.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {share && (
          <div className="border px-4 py-3 flex flex-wrap items-center justify-between gap-3"
               style={{ background: "#1F2226", borderColor: "#D8B23E" }} data-testid="fill-srn-banner">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#7A8590]">Rectification Mandate · Service Request Number</div>
              <div className="font-mono text-[15px] tracking-[0.16em] text-[#D8B23E] font-bold mt-0.5">{share.srn}</div>
            </div>
            <div className="text-right font-mono text-[10px] text-[#C4C8CC]">
              Issued to {share.recipient_email}{share.vinci_code ? ` · ${share.vinci_code}` : ""}
            </div>
          </div>
        )}
        <p className="text-[13px] text-[#4A5058] font-body italic">
          {form.narrative}
        </p>

        {/* Fields */}
        <section data-testid="submit-fields" className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {form.fields.map((f) => (
            <div key={f.key} className="bg-white/70 border p-3" style={{ borderColor: "#C4C8CC" }}>
              <label className="block font-mono text-[9px] uppercase tracking-[0.22em] text-[#6B6F76] mb-1">
                {f.label}{f.required && " *"}
              </label>
              {f.type === "textarea" ? (
                <textarea rows={3}
                          data-testid={`submit-field-${f.key}`}
                          value={values[f.key] || ""}
                          onChange={(e) => setField(f.key, e.target.value)}
                          className="w-full bg-transparent border border-[#C4C8CC] px-2 py-1.5 text-[12px] text-[#161A1E] font-serif" />
              ) : f.type === "select" ? (
                <select data-testid={`submit-field-${f.key}`}
                        value={values[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        className="w-full bg-white border border-[#C4C8CC] px-2 py-1.5 text-[12px] text-[#161A1E] font-serif">
                  <option value="">—</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type === "email" ? "email" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text"}
                       data-testid={`submit-field-${f.key}`}
                       value={values[f.key] || ""}
                       onChange={(e) => setField(f.key, e.target.value)}
                       className="w-full bg-transparent border border-[#C4C8CC] px-2 py-1.5 text-[12px] text-[#161A1E] font-serif" />
              )}
            </div>
          ))}
        </section>

        {/* Uploads */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#4A5058] mb-2">
            Compliance Capsule · Mandatory Uploads
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {form.uploads.map((u) => (
              <label key={u.key} className="bg-white/70 border p-4 cursor-pointer hover:border-[#4A5058] transition-colors"
                     style={{ borderColor: "#C4C8CC" }}
                     data-testid={`submit-upload-${u.key}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Upload size={12} className="text-[#4A5058]" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#4A5058]">
                    {u.key.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="font-serif text-[12px] text-[#161A1E]">
                  {u.label}
                </div>
                <input type="file" className="hidden"
                       onChange={(e) => setUpload(u.key, e.target.files?.[0])} />
                <div className="mt-2 text-[10px] font-mono text-[#7A8590] truncate">
                  {uploads[u.key]?.name || "No file selected"}
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Declarations */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#4A5058] mb-2">
            Declarations of Truth
          </div>
          <div className="space-y-2">
            {form.declarations.map((d, i) => (
              <label key={i} className="flex items-start gap-3 bg-white/70 border p-3 cursor-pointer"
                     style={{ borderColor: "#C4C8CC" }}>
                <input type="checkbox"
                       data-testid={`submit-declaration-${i}`}
                       checked={declarations[i] || false}
                       onChange={() => toggleDec(i)}
                       className="mt-1" />
                <span className="text-[12px] text-[#161A1E] font-body">{d}</span>
              </label>
            ))}
          </div>
        </section>

        <div className="flex justify-end gap-3 pt-4 border-t border-[#C4C8CC]/60">
          <Link to={share ? "/account" : "/admin/bureaucratic"}
                className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#6B6F76] hover:text-black px-4 py-2">
            Cancel
          </Link>
          <button
            data-testid="submit-file-instrument"
            onClick={submit} disabled={busy}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] bg-[#1F2226] text-[#E4E6E8] hover:bg-[#2C3138] px-5 py-2.5 rounded-sm disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            File Instrument
          </button>
        </div>
      </div>
    </div>
  );
}
